import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import Icon from '../../components/AppIcon';
import { createRFI, listRFIsByProject, updateRFIStatus, uploadRFIImages, listRFIImages, getRFIById, countRFIImages } from '../../services/rfiService';
import { supabase } from '../../lib/supabase';
import RFIDocument from '../../pdf/RFIDocument';
import useTranslation from '../../hooks/useTranslation';
import RFIQrtResponseModal from './RFIQrtResponseModal';
import RFITable from './components/RFITable';
import RFIDetailModal from './components/RFIDetailModal';
import StatusFilterDropdown from '../../components/ui/StatusFilterDropdown';
import { statusVariant } from '../../utils/statusVariant';
import { BADGE_VARIANTS } from '../../components/ui/Badge';
import { PROFESSIONS, professionLabel, statusLabel } from './components/rfiConstants';

// Tri du tableau, memorise par navigateur (meme principe qu'Extras).
const LS_KEY = 'rfi_table_state';
const SORT_KEYS = ['rfi_number', 'profession', 'subject', 'photos', 'status', 'created_at'];
const DEFAULT_SORT = { key: 'created_at', direction: 'desc' };
const loadSort = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (SORT_KEYS.includes(saved?.key) && ['asc', 'desc'].includes(saved?.direction)) return saved;
  } catch { /* ignore */ }
  return DEFAULT_SORT;
};

// ─── Carte KPI ────────────────────────────────────────────────
// Presentation LOCALE, volontairement non partagee : StatsCard sert 51 fois
// dans 13 pages (dont Daily Log et Quittances, 🔒 PRODUIT FINI) et
// StatusKPICard appartient a Extras, ou il formate de la monnaie. Ici la
// valeur est un NOMBRE de QRT.
//
// La couleur vient de la MEME source que la pastille de la ligne :
// statusVariant -> BADGE_VARIANTS. Une tuile et sa pastille ne peuvent donc
// pas diverger.
const RfiKpiCard = ({ label, value, variant, icon }) => {
  const v = BADGE_VARIANTS?.[variant] || BADGE_VARIANTS?.grey;
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm min-w-[120px]">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-medium text-gray-500">{label}</p>
        <span style={{ color: v?.text }}>
          <Icon name={icon} size={12} />
        </span>
      </div>
      <p className="text-sm font-semibold" style={{ color: v?.text }}>
        {value}
      </p>
    </div>
  );
};

// ─── Create RFI Modal ─────────────────────────────────────────
const CreateRFIModal = ({ onClose, onCreated, projectId }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ subject: '', profession: '', question: '' });
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handlePhotoSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const valid = files?.filter((f) => ['image/jpeg', 'image/png']?.includes(f?.type));
    setPhotos((prev) => [...prev, ...valid]);
    const newPreviews = valid?.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    URL.revokeObjectURL(previews?.[idx]);
    setPhotos((prev) => prev?.filter((_, i) => i !== idx));
    setPreviews((prev) => prev?.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.subject?.trim()) { setError(t('rfi.create.subjectRequired')); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: rfi, error: createError } = await createRFI({
        projectId,
        subject: form?.subject?.trim(),
        profession: form?.profession || null,
        question: form?.question?.trim() || null,
        createdBy: user?.id || null,
      });
      if (createError) throw createError;
      if (photos?.length > 0 && rfi?.id) {
        await uploadRFIImages(projectId, rfi?.id, photos);
      }
      onCreated?.(rfi);
      onClose?.();
    } catch (err) {
      setError(err?.message || t('rfi.create.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
              <Icon name="MessageSquare" size={18} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('rfi.create.title')}</h2>
              <p className="text-xs text-gray-500">{t('rfi.create.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <Icon name="X" size={18} className="text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Icon name="AlertCircle" size={15} />{error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('rfi.create.subjectLabel')} <span className="text-red-500">*</span></label>
            <input name="subject" value={form?.subject} onChange={handleChange} placeholder={t('rfi.create.subjectPlaceholder')} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder-gray-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('rfi.create.professionLabel')}</label>
            <select name="profession" value={form?.profession} onChange={handleChange} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white text-gray-700">
              <option value="">{t('rfi.create.professionPlaceholder')}</option>
              {PROFESSIONS?.map((p) => <option key={p?.value} value={p?.value}>{t(`rfi.professions.${p?.key}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('rfi.create.questionLabel')}</label>
            <textarea name="question" value={form?.question} onChange={handleChange} rows={4} placeholder={t('rfi.create.questionPlaceholder')} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder-gray-400 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t('rfi.create.photosLabel')}</label>
            <div onClick={() => fileInputRef?.current?.click()} className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors">
              <Icon name="ImagePlus" size={22} className="text-gray-400 mx-auto mb-1.5" />
              <p className="text-xs text-gray-500">{t('rfi.create.uploadHint')}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{t('rfi.create.uploadFormats')}</p>
            </div>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" multiple className="hidden" onChange={handlePhotoSelect} />
            {previews?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {previews?.map((src, idx) => (
                  <div key={idx} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    <img src={src} alt={t('rfi.create.photoPreviewAlt', { index: idx + 1 })} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(idx)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Icon name="X" size={14} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">{t('common.cancel')}</button>
          <button onClick={handleSubmit} disabled={loading} className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark disabled:opacity-60 rounded-lg transition-colors">
            {loading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Plus" size={15} />}
            {loading ? t('rfi.create.submitting') : t('rfi.create.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main RFI Page ────────────────────────────────────────────
const RFIPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [rfis, setRfis] = useState([]);
  // Nombre de photos de la question, par QRT (Storage, pas de colonne en base).
  const [photoCounts, setPhotoCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectName, setProjectName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [orgId, setOrgId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null); // { msg, tone: 'success' | 'error' }
  const [qrtTargetRfi, setQrtTargetRfi] = useState(null);
  const [sortConfig, setSortConfig] = useState(loadSort);
  // Fiche (consultation / modification)
  const [editingRfi, setEditingRfi] = useState(null);
  const [editingStartsInView, setEditingStartsInView] = useState(true);
  // La fiche charge la reponse a l'ouverture. Apres une modification de la
  // reponse il faut la remonter, sinon elle afficherait l'ancien texte.
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  const showToast = (msg, tone = 'success') => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchLogoAsBase64 = async (storagePath) => {
    try {
      const { data, error } = await supabase?.storage?.from('company-assets')?.createSignedUrl(storagePath, 60);
      if (error || !data?.signedUrl) return null;
      const response = await fetch(data?.signedUrl);
      if (!response?.ok) return null;
      const blob = await response?.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  useEffect(() => {
    if (projectId) {
      fetchRFIs();
      fetchProjectInfo();
    }
  }, [projectId]);

  const fetchProjectInfo = async () => {
    try {
      const { data: project } = await supabase?.from('projects')?.select('name, project_number, organization_id')?.eq('id', projectId)?.single();
      if (project?.name) setProjectName(project?.name);
      if (project?.project_number) setProjectNumber(project?.project_number);
      if (project?.organization_id) setOrgId(project?.organization_id);

      const { data: { user } } = await supabase?.auth?.getUser();
      if (user?.id) {
        const { data: profile } = await supabase?.from('user_profiles')?.select('company, full_name, logo_url')?.eq('id', user?.id)?.single();
        if (profile?.company) setCompanyName(profile?.company);
        else if (profile?.full_name) setCompanyName(profile?.full_name);
        if (profile?.logo_url) {
          const base64 = await fetchLogoAsBase64(profile?.logo_url);
          if (base64) setLogoUrl(base64);
        }
      }
    } catch (err) {
      console.warn('fetchProjectInfo error:', err);
    }
  };

  const fetchRFIs = async () => {
    setLoading(true);
    const { data, error } = await listRFIsByProject(projectId);
    if (!error) setRfis(data || []);
    setLoading(false);
    if (!error && data?.length) {
      countRFIImages(projectId, data?.map((r) => r?.id))?.then(setPhotoCounts);
    }
  };

  // Recompte les photos d'un seul QRT (apres creation ou enregistrement de la fiche).
  const refreshPhotoCount = (rfiId) => {
    if (!rfiId) return;
    countRFIImages(projectId, [rfiId])?.then((counts) => setPhotoCounts((prev) => ({ ...prev, ...counts })));
  };

  const handleCreated = (newRFI) => {
    if (newRFI) {
      setRfis((prev) => [newRFI, ...prev]);
      refreshPhotoCount(newRFI?.id);
    } else fetchRFIs();
  };

  const handleStatusChange = async (rfiId, newStatus) => {
    const { data } = await updateRFIStatus(rfiId, newStatus);
    if (data) {
      setRfis((prev) => prev?.map((r) => r?.id === rfiId ? { ...r, status: newStatus } : r));
      setEditingRfi((prev) => prev?.id === rfiId ? { ...prev, status: newStatus } : prev);
      showToast(t('rfi.statusUpdated') || 'Statut mis à jour');
    }
  };

  // Ouvre la modale de reponse en passant TOUJOURS l'enregistrement complet.
  // La ligne du tableau vient de select('*') : elle a bien response_text, mais
  // pas les photos ni les URL signees. Sans ce chargement, le pre-remplissage
  // perdrait silencieusement les photos.
  const openResponseModal = async (rfi) => {
    const { data } = await getRFIById(rfi?.id);
    setQrtTargetRfi(data || rfi);
  };

  const handleQrtSuccess = (updatedRfi) => {
    if (updatedRfi) {
      setRfis((prev) => prev?.map((r) => r?.id === updatedRfi?.id ? { ...r, ...updatedRfi } : r));
    }
    showToast(t('rfi.qrt.success') || 'Réponse soumise avec succès');
  };

  // Apres « Enregistrer » dans la fiche.
  const handleRfiUpdated = (updatedRfi) => {
    if (updatedRfi) {
      setRfis((prev) => prev?.map((r) => r?.id === updatedRfi?.id ? { ...r, ...updatedRfi } : r));
      refreshPhotoCount(updatedRfi?.id);
    }
    setEditingRfi(null);
    showToast(t('rfi.saved'));
  };

  // Suppression (confirmee dans RFITable). .select() : sous RLS, une
  // suppression refusee ne renvoie pas d'erreur, seulement zero ligne.
  const handleDeleteRfi = async (rfiId) => {
    const { data, error } = await supabase?.from('project_rfis')?.delete()?.eq('id', rfiId)?.select('id');
    if (error || !data?.length) {
      console.error('delete RFI failed:', error?.message || 'no row deleted');
      showToast(t('rfi.deleteFailed'), 'error');
      return;
    }
    setRfis((prev) => prev?.filter((r) => r?.id !== rfiId));
    showToast(t('rfi.deleted'));
  };

  // PDF du QRT (meme logique que l'ancien panneau de detail), pour le menu ⋮
  // et pour la fiche.
  const handleGeneratePDF = async (rfi) => {
    try {
      const { data: images } = await listRFIImages(projectId, rfi?.id);
      const blob = await pdf(
        <RFIDocument
          rfi={rfi}
          projectName={projectName}
          projectNumber={projectNumber}
          companyName={companyName}
          images={images || []}
          logoUrl={logoUrl}
          language={language}
        />
      )?.toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rfi?.rfi_number || 'RFI'}_${(rfi?.subject || 'document')?.replace(/\s+/g, '_')?.slice(0, 30)}.pdf`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation error:', err);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction = prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc';
      const next = { key, direction };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // KPI counts
  const countByStatus = (status) => rfis?.filter((r) => r?.status === status)?.length;
  const totalCount = rfis?.length;

  const rfiStatusFilterOptions = [
    { value: 'all', label: t('rfi.toolbar.filterAll'), dot: null },
    ...['draft', 'sent_for_approval', 'answered', 'in_review']?.map((value) => ({
      value,
      label: statusLabel(t, value),
      dot: BADGE_VARIANTS?.[statusVariant('rfi', value)]?.dot,
    })),
  ];

  const statusFiltered = statusFilter === 'all' ? rfis : rfis?.filter((r) => r?.status === statusFilter);
  const filtered = searchQuery?.trim()
    ? statusFiltered?.filter((r) => {
        const q = searchQuery?.toLowerCase()?.trim();
        const profLabel = professionLabel(t, r?.profession) || '';
        return r?.rfi_number?.toLowerCase()?.includes(q) || r?.subject?.toLowerCase()?.includes(q) || r?.profession?.toLowerCase()?.includes(q) || profLabel?.toLowerCase()?.includes(q);
      })
    : statusFiltered;

  // Profession et statut : tries sur le libelle affiche, pas sur la valeur stockee.
  const sortValue = (r, key) => {
    if (key === 'profession') return professionLabel(t, r?.profession) || '';
    if (key === 'status') return statusLabel(t, r?.status) || '';
    if (key === 'photos') return photoCounts?.[r?.id] ?? -1;
    return r?.[key] ?? '';
  };
  const sortedRfis = [...(filtered || [])]?.sort((a, b) => {
    const dir = sortConfig?.direction === 'asc' ? 1 : -1;
    const va = sortValue(a, sortConfig?.key);
    const vb = sortValue(b, sortConfig?.key);
    if (typeof va === 'string') return dir * va.localeCompare(String(vb), undefined, { numeric: true });
    return dir * (va > vb ? 1 : va < vb ? -1 : 0);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-xl shadow-lg ${toast?.tone === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          <Icon name={toast?.tone === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={16} />
          {toast?.msg}
        </div>
      )}
      {/* Header Block */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="MessageSquare" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-gray-900">{t('rfi.pageTitle')}</h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span onClick={() => navigate('/project-detail')} className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors">{projectNumber || '—'}</span>
              <span className="text-sm text-primary font-normal">-</span>
              <span onClick={() => navigate('/project-detail')} className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors">{projectName || t('common.loading')}</span>
            </div>
            <p className="text-xs text-gray-500">{t('rfi.subtitle')}</p>
          </div>
          <div className="flex-1 flex items-center justify-end gap-2">
            <RfiKpiCard
              label={t('rfi.status.draft') || 'Créé'}
              value={countByStatus('draft')}
              variant={statusVariant('rfi', 'draft')}
              icon="FileText"
            />
            <RfiKpiCard
              label={t('rfi.status.answered') || 'Répondu'}
              value={countByStatus('answered')}
              variant={statusVariant('rfi', 'answered')}
              icon="CheckCircle2"
            />
            {/* Total en dernier, comme la tuile Total d'Extras. */}
            <RfiKpiCard
              label={t('rfi.kpi.totalRfis')}
              value={totalCount}
              variant="green"
              icon="MessageSquare"
            />
          </div>
        </div>
      </div>
      {/* Search + Status filter + Add Button Row */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder={t('rfi.toolbar.searchPlaceholder')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                <Icon name="X" size={12} />
              </button>
            )}
          </div>
          <StatusFilterDropdown
            options={rfiStatusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm flex-shrink-0"
        >
          <Icon name="Plus" size={13} />
          {t('rfi.toolbar.newRfi')}
        </button>
      </div>
      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
        <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
          <RFITable
            rfis={sortedRfis}
            photoCounts={photoCounts}
            sortConfig={sortConfig}
            onSort={handleSort}
            onStatusChange={handleStatusChange}
            onOpenResponse={openResponseModal}
            onView={(rfi) => { setEditingStartsInView(true); setEditingRfi(rfi); }}
            onDownloadPDF={handleGeneratePDF}
            onDelete={handleDeleteRfi}
            t={t}
            language={language}
          />
        </div>
      </div>
      {/* Modals */}
      {showCreate && (
        <CreateRFIModal projectId={projectId} onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}
      {editingRfi && (
        <RFIDetailModal
          key={`${editingRfi?.id}-${detailRefreshKey}`}
          rfi={editingRfi}
          onClose={() => setEditingRfi(null)}
          onSave={handleRfiUpdated}
          onStatusChange={handleStatusChange}
          onOpenResponse={openResponseModal}
          onEditResponse={openResponseModal}
          onDownloadPDF={handleGeneratePDF}
          projectId={projectId}
          escapeDisabled={!!qrtTargetRfi}
          isEditMode={true}
          startInView={editingStartsInView}
        />
      )}
      {/* UNE seule modale de reponse pour tous les chemins (pastille, fiche).
          Elle decide elle-meme si elle est en modification, d'apres les
          donnees qu'elle recoit. */}
      {qrtTargetRfi && (
        <RFIQrtResponseModal
          rfi={qrtTargetRfi}
          projectId={projectId}
          orgId={orgId}
          onClose={() => setQrtTargetRfi(null)}
          onSuccess={(updatedRfi) => {
            setQrtTargetRfi(null);
            setEditingRfi((prev) => (prev?.id === updatedRfi?.id ? { ...prev, ...updatedRfi } : prev));
            setDetailRefreshKey((k) => k + 1);
            handleQrtSuccess(updatedRfi);
          }}
        />
      )}
    </div>
  );
};

export default RFIPage;
